import { getAppState, RootState } from 'redux/store'
import fileService, { buildDateFilter } from '@/modules/file/fileService'
import { updateQuickCounter, updatePagination, listError, getList, LIST_TYPES, QUERY_TYPES, setShallRefresh } from '@/redux/ducks/lists'
import documentConfig from '@/modules/document/config'
import request from '@/modules/base/util/request'
import getCollaborationRequestHeader from '@/modules/document/_helpers/getCollaborationRequestHeader'
import {
  errorDeletingDocumentListItem,
  errorFetchingDocumentListItems,
  fetchComplete,
  setTags,
  showLoader,
  updateDocumentListItem,
  updateDocumentListItems,
  updateFolderTree,
} from '@/redux/ducks/document/list'
import { TagService } from '@/modules/tags/tagService'
import { TAG_CATEGORY } from '@/modules/base/components/inputs/tag-select-input/tag-select-input'
// FLAG:useDocuAi — clear the uploader rows of documents that were just deleted.
import { removePendingByFileIds } from '@/redux/ducks/document/upload'
import { isUploadHardeningEnabled } from '@/redux/ducks/document/_helpers/uploadHardening'
import {
  ERROR_ADDING_TAGS,
  ERROR_DOWNLOADING_ZIP,
  ERROR_FETCHING_COLUMNS,
  ERROR_SETTING_IS_FAVORITE,
  ERROR_UPDATING_COLUMNS,
  FILTER_SETTINGS_UPDATED,
  LIST_SETTINGS_UPDATED,
  REVOKE_SELECTION,
  START_DOWNLOADING_ZIP,
  SUCCESS_DOWNLOADING_ZIP,
} from '../../document/actions'
import { makeFolderPath } from '@/modules/file/_helpers/makeFolderPath'
import lang from '@/lang'
import { csatFeatures } from '@/modules/base/components/csat-modal/constants'
import { folderCount } from '@/redux/ducks/document/_helpers/folderCount'
import openCsatModal from '@/modules/_helpers/openCsatModal'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { FILE_LIST_FILTER, errorCodes, experimentalFeatures } from '@/modules/base/util/constants'
import { pick } from 'lodash'
import { SORT_ORDER } from '@/enums/pagination'
import { ThunkAction } from 'redux-thunk'
import { Action } from 'redux'
import { Folder } from '@/modules/file/components/folder-nav/types'
import { DocumentListColumn, DocumentListRequestProps, FileInterface, Tag } from './_helpers/interfaces'
import { FILE_LIST_TYPE } from '@/modules/file/constants'
import moment from 'moment'
import { user } from 'ampersand-app'
import { refreshDocumentRecentFilter } from '@/modules/document/_helpers/refreshDocumentRecentFilter'
import { closeDocumentDrawer } from '@/redux/ducks/document/drawer'

const { DOCUMENTS_LIST_TYPE } = LIST_TYPES
type FolderExistsError = {
  body?: {
    meta?: {
      folderExists?: Array<{ code?: string }>
    }
  }
}

export const fetchListAction = (
  props: DocumentListRequestProps = {},
  cb?: (data: object) => void,
): ThunkAction<void, RootState, unknown, Action<string>> => {
  return async(dispatch, getState) => {
    const {
      includes,
      offset = 0,
      limit = 20,
      clear: paramClear,
      queryType = QUERY_TYPES.DATA,
      documentId,
    } = props
    if ([QUERY_TYPES.ALL, QUERY_TYPES.DATA].includes(queryType)) {
      dispatch(showLoader())
    }

    const params = props
    const { currentFolder, options, sortModel: sortModelState, pagination: paginAtion, selectedItems } = getState().document.list
    const url = `${documentConfig.routes.list}`
    const listFilters = getList(DOCUMENTS_LIST_TYPE)
    // FLAG:useDocuAi — relative dates must not stay frozen in persisted list settings.
    const { quickFilter, filterItems } = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
      ? refreshDocumentRecentFilter(listFilters)
      : listFilters
    const { filterModel: currentFilterModel = {}, pagination } = listFilters
    const { searchTerm, tagFilter = {} } = options
    const { tags = [], date = [] } = tagFilter
    const { sortOrder, orderBy } = paginAtion

    const sortModel
      = sortOrder && orderBy
        ? [{ sort: sortOrder, colId: orderBy }] // external
        : sortModelState || [{ sort: SORT_ORDER.DESC, colId: 'createdAt' }] // current // default
    const startRow = offset || 0
    const endRow = offset + limit || 20
    const clear = paramClear || pagination?.clear || false

    const folderId = (!currentFolder || currentFolder.isRoot) ? undefined : currentFolder.id

    const dateFilter = buildDateFilter(date)

    const filterModel = { ...currentFilterModel, ...quickFilter, ...dateFilter }
    const tagIds = tags.length ? tags.map(({ id }) => id) : undefined
    const dataToSend = {
      sortModel,
      filterModel,
      filterItems,
      searchTerm,
      startRow,
      endRow,
      folderId,
      tagIds,
      includes,
      selected: selectedItems.map(({ id }) => id),
      queryType,
      documentId,
      businessPartnerUnlinkedOnly: props.businessPartnerUnlinkedOnly,
    }
    return request(url, {
      method: 'POST',
      auth: true,
      isConcurrent: true,
      data: dataToSend,
      headers: getCollaborationRequestHeader(),
      requestId: `documentListData-${queryType}`,
      timeout: 2 * 60 * 60 * 1000,
    })
      .then((response) => {
        const { data, meta, folders } = response.body

        if ([QUERY_TYPES.ALL, QUERY_TYPES.DATA].includes(queryType)) {
          dispatch(updatePagination(DOCUMENTS_LIST_TYPE, meta))
          dispatch(fetchComplete({ ...meta, data, clear, folders, offset }))
          params?.successCallback?.(data, meta.count)
          cb?.({ data, meta })
        }
        if ([QUERY_TYPES.ALL, QUERY_TYPES.COUNTER].includes(queryType)) {
          dispatch(updateQuickCounter(DOCUMENTS_LIST_TYPE, meta))
        }
        return { data, meta }
      })
      .catch((error) => {
        dispatch(listError(DOCUMENTS_LIST_TYPE, error))
        params?.successCallback?.({ data: [], meta: { count: 0 } })
        return { data: [], meta: { count: 0 } }
      })
  }
}

export const fetchListSetting = (): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    request(documentConfig.listSetting, {
      auth: true,
      isConcurrent: true,
      method: 'GET',
    })
      .then((response) => {
        if (response?.body?.data?.columns || response?.body?.data?.autoSizeColumns) {
          dispatch({
            type: LIST_SETTINGS_UPDATED,
            columns: response.body.data.columns,
            autoSizeColumns: true, // option for disabling auto size columns is hidden so for now always set it to true
          })
        }
        dispatch({
          type: FILTER_SETTINGS_UPDATED,
          isFilterOpen: response?.body?.meta,
        })
      })
      .catch((error) => {
        dispatch({ type: ERROR_FETCHING_COLUMNS, error })
      })
  }
}

export const updateColumnSettings = (
  data: DocumentListColumn[],
): ThunkAction<void, RootState, unknown, Action<string>> => {
  return async(dispatch) => {
    const sendRequest = request(`${documentConfig.listSetting}/columns`, {
      auth: true,
      method: 'PUT',
      data: data
        .filter((item) => item.name && item.name !== 'select')
        .map((column) => pick(column, ['name', 'label', 'active', 'width', 'required'])),
    })

    sendRequest
      .then((response) => {
        if (response?.body?.data) {
          dispatch({
            type: LIST_SETTINGS_UPDATED,
            columns: response.body.data,
          })
        }
      })
      .catch((error) => {
        dispatch({ type: ERROR_UPDATING_COLUMNS, error })
      })
  }
}

export const deleteFiles = (files: FileInterface[]): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch, getState) => {
    dispatch(showLoader())
    const ids = files.filter(({ isAssigned }) => isAssigned === false).map(({ id }) => id)
    fileService
      .deleteFiles(ids)
      .then(() => {
        toast.showToast({
          type: 'success',
          message: ids?.length > 1 ? lang.documentDeleteSuccessMessageMulti : lang.documentDeleteSuccessMessageSingle,
        })

        // FLAG:useDocuAi — the document is gone, so its upload row is a ghost. Nothing else ever
        // removed it: the row's own ✕ is the only caller of removePending, and that ✕ now fails
        // (cancel-upload cannot find the File row any more), so the row and the "n of m complete"
        // counter survived until a reload.
        if (isUploadHardeningEnabled()) {
          dispatch(removePendingByFileIds(ids))
        }
        const listState = getState().document.list
        const { listDisplayType, pagination: { limit, offset } } = listState

        dispatch(closeDocumentDrawer())

        if (listDisplayType === FILE_LIST_TYPE.thumbs) {
          dispatch(fetchListAction({ clear: true, queryType: QUERY_TYPES.ALL, offset: 0, limit: limit + offset }))
        }
        if (listDisplayType === FILE_LIST_TYPE.list) {
          dispatch(setShallRefresh(LIST_TYPES.DOCUMENTS_LIST_TYPE))
          dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
        }
        dispatch(fetchFolders())
      })
      .catch((error: object) => dispatch(errorDeletingDocumentListItem(error)))
  }
}

export const updateFileInfo = (file: FileInterface): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    return fileService
      .updateFileInfo(file)
      .then(async({ body: data }) => {
        return dispatch(updateDocumentListItem({ ...data.data, thumbnailUrl: file.thumbnailUrl }))
      })
      .catch((error) => {
        dispatch(errorFetchingDocumentListItems(error))
        throw error
      })
  }
}

export const fetchTags = (): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    TagService.fetchTags(TAG_CATEGORY.DOCUMENTS).then((response) => {
      const {
        body: { data: options },
      } = response
      dispatch(setTags(options))
    })
  }
}

export const createFolder = (
  name: string,
  currentFolder,
  activeFilter: string,
  folders: Folder,
  successMessage?: string,
  permissions: Array<{ id: string, type: string }> = [],
): Promise<void> => {
  const increaseFolderPathSuffix = (folderPath: string): string => {
    const segments = folderPath.split('/')
    const currentName = segments.pop() || ''
    const suffixMatch = currentName.match(/^(.*) \((\d+)\)$/)
    const nextName = suffixMatch
      ? `${suffixMatch[1]} (${Number(suffixMatch[2]) + 1})`
      : `${currentName} (1)`
    return [...segments, nextName].join('/')
  }

  const createFolderWithDuplicateFallback = async(): Promise<{ createdFolders: unknown, candidatePath: string }> => {
    let candidatePath = makeFolderPath({ name }, currentFolder)
    let lastFolderExistsError: unknown = null
    for (let attempt = 0; attempt < 50; attempt += 1) {
      try {
        const createdFolders = await fileService.mkDir(candidatePath, activeFilter, requestPermissions)
        return { createdFolders, candidatePath }
      } catch (error: unknown) {
        const folderExistsError = error as FolderExistsError
        const isFolderExistsError = folderExistsError?.body?.meta?.folderExists?.[0]?.code === errorCodes.NOT_ALLOWED
        if (!isFolderExistsError) {
          throw error
        }
        lastFolderExistsError = error
        candidatePath = increaseFolderPathSuffix(candidatePath)
      }
    }
    throw lastFolderExistsError
  }

  const requestPermissions = permissions.filter((permission) => permission.type !== 'owner')

  console.log('Creating OpenFGA tuples:', {
    folderName: name,
    parentFolderId: currentFolder?.id,
    permissions: requestPermissions,
  })

  return createFolderWithDuplicateFallback()
    .then(({ createdFolders, candidatePath }) => {
      // Assuming the new folder is the last created one
      const newFolder = createdFolders?.children?.slice(-1)[0]
      const createdFolderName = candidatePath.split('/').slice(-1)[0] || name

      console.log('OpenFGA tuples created:', {
        folderId: newFolder?.id,
        folderPath: newFolder?.path,
        tuples: requestPermissions.map(p => ({
          user: p.id,
          relation: p.type,
          object: newFolder?.id,
        })),
      })

      toast.showToast({
        type: 'success',
        title: activeFilter === 'all' ? undefined : lang.documents.uploadSuccessTitle,
        message:
          activeFilter === 'all'
            ? lang.documents.folder.createSuccess
            : lang.documents.folder.createFolderMessage(createdFolderName),
      })
    })
    .then(() => {
      if (folderCount(folders.children) >= 2) {
        openCsatModal({ feature: csatFeatures.manageFolders })
      }
    })
    .catch(({ body: { meta } }) => {
      let message = lang.documents.folder.createFailed
      if (meta.name?.[0].code === errorCodes.TOO_LARGE) {
        message = lang.documents.folder.nameTooLong
      } else if (meta.name?.[0].code === errorCodes.REQUIRED) {
        message = lang.documents.folder.nameTooShort
      } else if (meta.systemFolder?.[0].code === errorCodes.NOT_ALLOWED) {
        message = lang.documents.folder.systemFolderNoChildren
      }
      toast.showToast({ type: 'error', message })
    })
}

export const updateDocumentTags = (
  fileIds: string[],
  tagId: string,
  remove = false,
): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    return fileService
      .setTags(fileIds, tagId, remove)
      .then(({ body: { data } }) => {
        dispatch(updateDocumentListItems(data))
      })
      .catch((error) =>
        dispatch({
          type: ERROR_ADDING_TAGS,
          error,
        }),
      )
  }
}

export const addNewTag = (tag: Tag): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch, getState) => {
    const state = getState().document.list
    return fileService
      .addNewTag(tag)
      .then(({ body: { data } }) => {
        const { label, id } = data[0]
        dispatch(setTags([...state.tagOptions, { ...tag, id, label }]))
      })
      .catch((error) =>
        dispatch({
          type: ERROR_ADDING_TAGS,
          error,
        }),
      )
  }
}

export const downloadZip = (files, isTaxAccountant) => {
  return (dispatch, getState) => {
    const { dmsZipDownloadUrl } = getState().tenant.settings
    const isDownloading = getAppState().document.list.isDownloading

    if (isDownloading) return

    toast.showToast(({ type: 'info', message: lang.documents.zipDownloadStarted }))

    dispatch({ type: START_DOWNLOADING_ZIP })

    const minioIds = files
      .filter(({ minioId }) => !!minioId)
      .map(({ minioId }) => minioId)

    const promise = dmsZipDownloadUrl
      ? fileService.dmsDownloadZip(minioIds, `Download-${moment().format('DD.MM.YYYY - HH:mm')}`)
      : fileService.downloadZip(files.map(({ id }) => id))

    return promise
      .then(() => {
        dispatch({
          type: SUCCESS_DOWNLOADING_ZIP,
          isTaxAccountant,
        })
        dispatch({
          type: REVOKE_SELECTION,
        })
      })
      .catch((error) => {
        dispatch({
          type: ERROR_DOWNLOADING_ZIP,
          error,
          isTaxAccountant,
        })
      })
  }
}

export const downloadFolderAsZip = (folderPath) => {
  return async(dispatch, getState) => {
    dispatch({
      type: START_DOWNLOADING_ZIP,
      isDownloading: true,
    })

    try {
      const { fileIds, minioIds } = await fileService.getFileIds(folderPath)
      const { dmsZipDownloadUrl } = getState().tenant.settings
      const path = folderPath.replace(/\/+$/, '')
      const folderName = path.split('/')
      let promise
      if (dmsZipDownloadUrl) {
        promise = fileService.dmsDownloadZip(minioIds, `Download${folderName ? '-' + folderName[folderName.length - 1] : ''}-${moment().format('DD.MM.YYYY - HH:mm')}`)
      } else {
        promise = fileService.downloadZip(fileIds, folderName)
      }

      await promise

      dispatch({
        type: SUCCESS_DOWNLOADING_ZIP,
        isDownloading: false,
      })
    } catch (error) {
      dispatch({
        type: ERROR_DOWNLOADING_ZIP,
        error,
        isDownloading: false,
      })
    }
  }
}

export const setDocumentsFavorite = (
  files: FileInterface[],
  isFavorite: boolean,
): ThunkAction<void, RootState, unknown, Action<string>> => {
  const { filter } = getList(DOCUMENTS_LIST_TYPE)
  return (dispatch, getState) => {
    const {
      options: { searchTerm },
    } = getState().document.list
    return fileService
      .batchSetFavorite(
        files.map(({ id }) => id),
        isFavorite,
        { filter, searchTerm },
      )
      .then(() => {
        if (filter === FILE_LIST_FILTER.FAVORITE) {
          const { listDisplayType } = getState().document.list
          if (listDisplayType === FILE_LIST_TYPE.thumbs) {
            dispatch(fetchListAction({ clear: true, queryType: QUERY_TYPES.ALL }))
          }
          if (listDisplayType === FILE_LIST_TYPE.list) {
            dispatch(setShallRefresh(LIST_TYPES.DOCUMENTS_LIST_TYPE))
            dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
          }
        } else {
          dispatch(fetchListAction({ queryType: QUERY_TYPES.COUNTER }))
        }

        dispatch(updateDocumentListItems(files.map((file) => ({ ...file, isFavorite }))))
      })
      .catch((error) => {
        dispatch({
          type: ERROR_SETTING_IS_FAVORITE,
          error,
        })
      })
  }
}

export const setFolderFavorite = (
  folders: Folder[],
  isFavorite: boolean,
): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    return fileService
      .batchSetFolderFavorite(
        folders.map(({ id }) => id),
        isFavorite,
      )
      .then(() => {
        dispatch(fetchFolders())
      })
      .catch((error) => {
        console.error({ error })
        dispatch({
          type: ERROR_SETTING_IS_FAVORITE,
          error,
        })
      })
  }
}

export const fetchFolders = (): ThunkAction<void, RootState, unknown, Action<string>> => {
  return (dispatch) => {
    return request(`${documentConfig.routes.folders}`, {
      method: 'GET',
      auth: true,
      isConcurrent: true,
      headers: getCollaborationRequestHeader(),
    })
      .then((response) => {
        const { data, meta } = response.body

        dispatch(updateFolderTree(data))
        return { data, meta }
      })
      .catch((error) => {
        dispatch(listError(DOCUMENTS_LIST_TYPE, error))
        return { data: [], meta: { count: 0 } }
      })
  }
}
